import { isViewableInBrowser } from '@obras/shared';
import { Download, Eye, Loader2 } from 'lucide-react';
import { useState } from 'react';

import { api } from '../../lib/api.js';
import { cn } from '../../lib/cn.js';

/*
 * As peças de uma linha de arquivo, usadas onde quer que um arquivo apareça:
 * no dossiê da obra e na lista de documentos de cada relatório. Um só lugar
 * para que o selo, o olho e o download sejam iguais nas duas telas.
 */

const ICON = { strokeWidth: 1.5 } as const;

/**
 * A extensão no lugar de um ícone por formato.
 *
 * Um `.sldprt` não tem ícone que alguém reconheça, e a sigla é exatamente o que
 * quem trabalha com o arquivo procura na lista.
 */
export function Selo({ extension }: { extension: string }) {
  return (
    <span className="tabular grid h-9 w-12 shrink-0 place-items-center rounded-md bg-muted font-mono text-[10px] tracking-wide text-muted-foreground uppercase">
      {extension || '—'}
    </span>
  );
}

/**
 * Abre o arquivo numa aba.
 *
 * Só aparece onde a aba realmente mostra o arquivo — PDF e imagem, que são
 * guardados com o tipo verdadeiro. Todo o resto vai para o storage como
 * `application/octet-stream` de propósito, e "visualizar" um `.dwg` abriria uma
 * aba que baixa o arquivo assim mesmo: o botão mentiria.
 */
export function BotaoVer({
  url,
  extension,
  originalName,
  pequeno = false,
}: {
  url: string;
  extension: string;
  originalName: string;
  pequeno?: boolean;
}) {
  if (!isViewableInBrowser(extension)) return null;

  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      title={`Visualizar ${originalName}`}
      aria-label={`Visualizar ${originalName}`}
      className={cn(
        'grid shrink-0 place-items-center rounded-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground',
        pequeno ? 'size-8' : 'size-9',
      )}
    >
      <Eye {...ICON} size={pequeno ? 14 : 16} aria-hidden />
    </a>
  );
}

/**
 * Baixa o arquivo pela API.
 *
 * O link não pode apontar para a rota direto: o token de acesso vive em memória
 * e viaja no cabeçalho, que um `<a href>` não manda. Então o arquivo vem por
 * `fetch` e o download dispara a partir do blob — o mesmo caminho dos relatórios
 * em PDF.
 *
 * `documentId` é o id do anexo do dossiê ou, para um arquivo enviado num
 * relatório, o id da mídia: a rota resolve os dois.
 */
export function BotaoBaixar({
  obraId,
  documentId,
  originalName,
  pequeno = false,
}: {
  obraId: string;
  documentId: string;
  originalName: string;
  pequeno?: boolean;
}) {
  const [baixando, setBaixando] = useState(false);

  return (
    <button
      type="button"
      disabled={baixando}
      title={`Baixar ${originalName}`}
      aria-label={`Baixar ${originalName}`}
      onClick={() => {
        setBaixando(true);
        void api
          .download(`/obras/${obraId}/documentos/${documentId}/arquivo`, originalName)
          .finally(() => setBaixando(false));
      }}
      className={cn(
        'grid shrink-0 place-items-center rounded-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50',
        pequeno ? 'size-8' : 'size-9',
      )}
    >
      {baixando ? (
        <Loader2 {...ICON} size={pequeno ? 14 : 16} className="animate-spin" aria-hidden />
      ) : (
        <Download {...ICON} size={pequeno ? 14 : 16} aria-hidden />
      )}
    </button>
  );
}
